import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import Spinner from "../../components/ui/spinner";
import { Copy, Trash2, FileCode, Eye, Check } from "lucide-react";
import { PrototypeDTO } from "../../data/service/prototype-service";

export function PrototypeListView({
    prototypes,
    isLoading,
    onUpload,
    onDelete,
}: {
    prototypes: PrototypeDTO[];
    isLoading: boolean;
    onUpload: () => void;
    onDelete: (slug: string) => void;
}) {
    const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

    const copyUrl = (slug: string) => {
        void navigator.clipboard.writeText(`${window.location.origin}/prototypes/v/${slug}`);
        setCopiedSlug(slug);
        setTimeout(() => setCopiedSlug(null), 2000);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Your prototypes</h1>
                    <p className="text-muted-foreground">
                        Upload HTML files and share them with your team or external stakeholders.
                    </p>
                </div>
                <Button onClick={onUpload}>+ Publish prototype</Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Spinner className="h-8 w-8" />
                </div>
            ) : prototypes.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <FileCode className="mb-4 h-12 w-12 text-muted-foreground" />
                        <p className="text-lg font-medium">No prototypes yet</p>
                        <p className="text-sm text-muted-foreground">
                            Publish your first HTML prototype to share with your team.
                        </p>
                        <Button onClick={onUpload} className="mt-4">
                            + Publish prototype
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {prototypes.map((p) => (
                        <Card key={p.slug} className="transition-shadow hover:shadow-md">
                            <CardContent className="flex items-center justify-between py-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                                        <FileCode className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{p.name}</p>
                                        <p className="text-sm text-muted-foreground">
                                            /{p.slug} &middot; {new Date(p.createdAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => copyUrl(p.slug)}>
                                        {copiedSlug === p.slug ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => window.open(`/prototypes/v/${p.slug}`, "_blank")}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => onDelete(p.slug)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
